import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { PolicyForm2, type PolicyFormState } from './policy-form2';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useAccounts } from '@/hooks/account/useAccounts';
import { useListGroups } from '@/hooks/organization/use-list-groups';
import { useListMembers } from '@/hooks/organization/use-list-members';
import type {
  Policy,
  PolicyTokenTransferCondition,
} from '@mls-wallet/shared-types';
import { buildPolicyTokenTransferCondition } from './build-policy-token-transfer-condition';
import { isPolicyFormSubmittable } from './policy-form-validation';

interface NewPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPolicyCreated: (policy: Policy) => void;
}

export function NewPolicyDialog({
  open,
  onOpenChange,
  onPolicyCreated,
}: NewPolicyDialogProps) {
  const [formData, setFormData] = useState<PolicyFormState | null>(null);

  // Fetch organization data
  const { organization, isLoading: isOrgLoading } = useMyOrganization();

  // Fetch accounts, groups, and members
  const { accounts, isLoading: isAccountsLoading } = useAccounts();
  const { groups: rawGroups, isLoading: isGroupsLoading } = useListGroups(
    organization?.id ?? null
  );
  const { members: rawMembers, isLoading: isMembersLoading } = useListMembers(
    organization?.id ?? null
  );

  // Transform groups and members to the expected format
  const groups = useMemo(
    () =>
      rawGroups.map((group) => ({
        id: group.id,
        name: group.name,
        memberCount: group.members.length,
        type: 'group' as const,
      })),
    [rawGroups]
  );

  const members = useMemo(
    () =>
      rawMembers.map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        type: 'individual' as const,
      })),
    [rawMembers]
  );

  const handleFormDataChange = (data: PolicyFormState) => {
    setFormData(data);
  };

  const handleSubmit = () => {
    if (!formData || !organization || !formData.networkId) return;

    // Generate a temporary ID for the new policy (for frontend tracking only)
    // This will be set to null before sending to the backend
    const tempId = `temp-policy-${crypto.randomUUID()}`;

    // Build token transfer condition
    let tokenTransferCondition: PolicyTokenTransferCondition | null = null;
    if (formData.policyTransactionType === 'TOKEN_TRANSFER') {
      tokenTransferCondition = buildPolicyTokenTransferCondition(formData);
    }

    // Build contract interaction condition
    let contractInteractionCondition = null;
    if (formData.policyTransactionType === 'CONTRACT_INTERACTION') {
      const hasConditions =
        formData.contractAddressSelection.type === 'specific' ||
        formData.functionSelection.proxyFunctions.length > 0 ||
        formData.functionSelection.implementationFunctions.length > 0;

      if (hasConditions) {
        const contractAddressesArray: Array<{
          contractAddress: string;
          proxyFunctions: Array<{ name: string; selector: string }>;
          implementationFunctions: Array<{ name: string; selector: string }>;
        }> = [];

        if (formData.contractAddressSelection.type === 'specific') {
          contractAddressesArray.push({
            contractAddress: formData.contractAddressSelection.address,
            proxyFunctions: formData.functionSelection.proxyFunctions,
            implementationFunctions:
              formData.functionSelection.implementationFunctions,
          });
        }

        contractInteractionCondition = contractAddressesArray;
      }
    }

    // Map form accounts to policy accounts
    const policyAccounts = formData.selectedAccounts.map((account) => ({
      id: account.id,
      name: account.name,
      address: account.address,
    }));

    // Convert selectedInitiator to initiatorSetting format
    let initiatorSetting = null;
    if (formData.selectedInitiator) {
      if (formData.selectedInitiator.type === 'individual') {
        initiatorSetting = {
          type: 'user' as const,
          userId: formData.selectedInitiator.id,
          userName: formData.selectedInitiator.name,
        };
      } else if (formData.selectedInitiator.type === 'group') {
        // Look up the full group to get members
        const fullGroup = rawGroups.find(
          (g) => g.id === formData.selectedInitiator?.id
        );
        initiatorSetting = {
          type: 'group' as const,
          groupId: formData.selectedInitiator.id,
          groupName: formData.selectedInitiator.name,
          members:
            fullGroup?.members.map((m) => ({ id: m.id, name: m.name })) ?? [],
        };
      }
    }

    // Convert selectedApprover to approverSetting format
    let approverSetting = null;
    if (
      formData.policyType === 'MANUAL_APPROVAL' &&
      formData.selectedApprover
    ) {
      if (formData.selectedApprover.type === 'individual') {
        approverSetting = {
          type: 'user' as const,
          userId: formData.selectedApprover.id,
          userName: formData.selectedApprover.name,
        };
      } else if (formData.selectedApprover.type === 'group') {
        // Look up the full group to get members
        const fullGroup = rawGroups.find(
          (g) => g.id === formData.selectedApprover?.id
        );
        approverSetting = {
          type: 'group' as const,
          groupId: formData.selectedApprover.id,
          groupName: formData.selectedApprover.name,
          threshold: formData.approverThreshold ?? 1,
          members:
            fullGroup?.members.map((m) => ({ id: m.id, name: m.name })) ?? [],
        };
      }
    }

    // Build the complete Policy object based on transaction type
    let newPolicy: Policy;
    const basePolicy = {
      id: tempId,
      onchainId: '', // Placeholder - real value assigned by backend on creation
      name: formData.policyName,
      description: formData.policyDescription || null,
      type: formData.policyType,
      networkId: formData.networkId,
      organizationId: organization.id,
      accounts: policyAccounts,
      limitation: formData.limitation,
      initiatorSetting,
      approverSetting,
      isDeleted: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    if (formData.policyTransactionType === 'TOKEN_TRANSFER') {
      newPolicy = {
        ...basePolicy,
        transactionType: 'TOKEN_TRANSFER',
        tokenTransferCondition,
      };
    } else if (formData.policyTransactionType === 'CONTRACT_INTERACTION') {
      newPolicy = {
        ...basePolicy,
        transactionType: 'CONTRACT_INTERACTION',
        contractInteractionCondition,
      };
    } else {
      newPolicy = {
        ...basePolicy,
        transactionType: 'ANY',
      };
    }

    // Pass the policy back to parent via callback
    onPolicyCreated(newPolicy);
    onOpenChange(false);
  };

  const isLoading =
    isOrgLoading || isAccountsLoading || isGroupsLoading || isMembersLoading;

  const canSubmit = isPolicyFormSubmittable(formData) && !isLoading;

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      noPadding
      title="Create a new policy"
      footer={
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          <Button
            size="lg"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="lg" onClick={handleSubmit} disabled={!canSubmit}>
            Create Policy
          </Button>
        </div>
      }
      bodyContainerClassName="bg-muted"
    >
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
        <PolicyForm2
          onDataChange={handleFormDataChange}
          sectionVariant="muted"
          accounts={accounts}
          groups={groups}
          members={members}
        />
      )}
    </NestedFullscreenDialog>
  );
}
